/**
 * AZ ÜGYFÉL-KAPCSOLATTARTÓ ŰRLAPJA — csak FELVÉTEL (#32).
 *
 * ═══ AMI SZÁNDÉKOSAN NINCS RAJTA ═══
 *
 *   • A SZEREPKÖR ÉS A HATÓKÖR. Ezen az úton mindkettő rögzített: a
 *     kapcsolattartó a cégéhez kötött identitás. Egy legördülő, aminek egy
 *     eleme van, nem választás — csak azt sugallná, hogy lehetne más is.
 *   • A CÉG. Az a NYITOTT cég, a fejlécben a neve áll; a kérésben az útvonal
 *     része (`useCompanyContacts`).
 *   • A JELSZÓ. A kapcsolattartó varázslinket kap, és maga állít jelszót — a
 *     felvevő nem lát belépést érő titkot (Viktor, 2026-09-11).
 *   • A MÓDOSÍTÁS. A név és az e-mail cím a belépés azonosítója; ma nincs út,
 *     ami átírja őket — sem az irodai felhasználónál (`UserForm`), sem itt.
 */

import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactsState, ContactsStatus } from './useCompanyContacts';

export interface ContactFormProps {
  /** A cég NEVE — a címben, hogy a felvevő lássa, kihez veszi fel. */
  companyName: string;
  state: ContactsState;
  /** A nézet zárója — a hibát is törli, és az `Escape` is ezt hívja. */
  onDone: () => void;
}

export function ContactForm({ companyName, state, onDone }: ContactFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  /**
   * A horog állapota MEGOSZTOTT a lista és az űrlap között: egy korábbi sor-
   * művelet hibája nem jelenhet meg egy frissen megnyitott űrlapon.
   */
  const [submitted, setSubmitted] = useState(false);
  const busy = state.status === 'saving';

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitted(true);
    state.create({ name: name.trim(), email: email.trim() }, onDone);
  }

  return (
    <form className="card settings__form" onSubmit={onSubmit}>
      <h2>{t('contacts.form.createTitle', { company: companyName })}</h2>

      <FormError status={submitted ? state.status : null} detail={state.detail} />

      <label htmlFor="contact-name">{t('contacts.form.name')}</label>
      <input
        id="contact-name"
        required
        // A billentyűzet-első felület a fókusszal kezd, nem az egérrel.
        // biome-ignore lint/a11y/noAutofocus: az űrlap a képernyő egyetlen tartalma
        autoFocus
        value={name}
        onChange={(event) => setName(event.target.value)}
      />

      <label htmlFor="contact-email">{t('contacts.form.email')}</label>
      <input
        id="contact-email"
        type="email"
        required
        aria-describedby="contact-email-hint"
        value={email}
        onChange={(event) => setEmail(event.target.value)}
      />
      <p id="contact-email-hint" className="hint">
        {t('contacts.form.emailHint')}
      </p>

      <div className="settings__toolbar">
        <button type="submit" className="settings__primary" disabled={busy}>
          {busy ? t('contacts.form.saving') : t('contacts.form.create')}
        </button>
        <button type="button" onClick={onDone} disabled={busy}>
          {t('contacts.form.cancel')}
        </button>
      </div>
    </form>
  );
}

/**
 * A MENTÉS HIBÁJA. A 409 és a 403 a SZERVER SZÖVEGÉT mutatja, szó szerint —
 * „ezzel az e-mail címmel már van felhasználó az irodában", „a cég inaktív".
 * Egy saját „nem sikerült" ezeknél elnyelné a teendőt: az első esetben másik
 * címet kell keresni, a másodikban előbb a céget visszakapcsolni.
 */
function FormError({ status, detail }: { status: ContactsStatus | null; detail: string }) {
  const { t } = useTranslation();

  if (status === 'conflict' || status === 'refused') {
    return (
      <p className="error" role="alert">
        {detail}
      </p>
    );
  }
  if (status === 'invalid') {
    return (
      <p className="error" role="alert">
        {t('contacts.form.invalid', { detail })}
      </p>
    );
  }
  if (status === 'failed') {
    return (
      <p className="error" role="alert">
        {t('contacts.form.failed')}
      </p>
    );
  }
  return null;
}
